<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ECHANNEL_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            text="Export"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport">
              CSV
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              Excel
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="eChannelTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        <p class="mb-0">
          {{ data.value ? toDateWithYear(data.value) : '' }}
        </p>
      </template>
      <template #cell(transactionDate)="data">
        <p class="mb-0">
          {{ data.value ? toDateWithCustomTime(data.value) : '' }}
        </p>
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(debit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>

      <template #cell(credit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      title="Filters"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="eChannelTransactionRules">
        <b-form>
          <b-row class="match-height">
            <!-- Loan Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                label="Account Number"
                placeholder="Enter Account Number"
                type="text"
              />
            </b-col>
            <!-- Loan Account Number -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                label="Branch"
                placeholder="Select"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
          </b-row>

          <b-row class="match-height">
            <!-- Posting Reference -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.postingReferenceNumber"
                label="Posting Reference"
                placeholder="Enter Posting Reference"
                type="text"
              />
            </b-col>
            <!-- Posting Reference -->
            <!-- Instrument Number -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.instrumentNumber"
                label="Instrument Number"
                placeholder="Enter Instrument Number"
                type="text"
              />
            </b-col>
            <!-- Instrument Number -->

            <!-- Financial Date From -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                label="Financial Date (From)"
                error-name="Financial Date From"
                :config="datePickerConfig"
                placeholder="Date From"
              />
            </b-col>
            <!-- Financial Date From -->
            <!-- Financial Date To -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                label="Financial Date (To)"
                error-name="Financial Date To"
                :config="datePickerConfig"
                placeholder="Date To"
              />
            </b-col>
            <!-- Financial Date To -->

            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="transaction-date-from"
                v-model="filter.startDate"
                :min-date="new Date()"
                label="Transaction Date (From)"
                error-name="Transaction Date From"
                :config="datePickerConfig"
                placeholder="Transaction Date From"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="transaction-date-to"
                v-model="filter.endDate"
                :min-date="new Date()"
                label="Transaction Date (To)"
                error-name="Transaction Date To"
                :config="datePickerConfig"
                placeholder="Transaction Date To"
              />
            </b-col>
            <!-- End Date -->

            <!-- Sort Code -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.entryCode"
                label="Sort Code"
                placeholder="Enter Sort Code"
                type="text"
              />
            </b-col>
            <!-- Sort Code -->
            <!-- Transaction Channel -->
            <b-col md="6">
              <SelectField
                id="transaction-channel"
                v-model="filter.transactionChannel"
                :reduce="option => option.value"
                label="Transaction Channel"
                placeholder="Select"
                :options="transactionChannelOptions"
                item="label"
              />
            </b-col>
            <!-- Transaction Channel -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import * as FileSaver from 'file-saver'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import toNumber from '../../../../helpers/toNumber'
import {
  toDateWithYear, toYearWithDate, toDateWithCustomTime,
} from '@/helpers/formatDate'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      toFormatAmount,
      formatAmount,
      toDateWithYear,
      toDateWithCustomTime,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: 'Save',
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: 'E-Channel Transactions Report',
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true },
        { key: 'transactionDate', sortable: true },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'branch', sortable: true, label: 'Branch Name' },
        { key: 'debit', sortable: true },
        { key: 'credit', sortable: true },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Ref No.' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument No.' },
        { key: 'entryCode', sortable: true, label: 'Entry Code' },
        { key: 'narration', sortable: true },
        { key: 'transactionType', sortable: true, label: 'Transaction Type' },
        { key: 'transactionChannel', sortable: true, label: 'Trnx Channel' },
        { key: 'stan', sortable: true },
        { key: 'hiddenPan', sortable: true },
        { key: 'terminalID', sortable: true, label: 'Terminal ID' },
      ],

      filter: {
        accountNumber: '',
        branchCode: '',
        postingReferenceNumber: '',
        instrumentNumber: '',
        financialDateFrom: null,
        financialDateTo: null,
        startDate: null,
        endDate: null,
        entryCode: '',
        transactionChannel: '',
      },
      transactionChannelOptions: [
        { value: 'ATM', label: 'ATM' },
        { value: 'POS', label: 'POS' },
        { value: 'Web', label: 'Web' },
        { value: 'Mobile', label: 'Mobile' },
        { value: 'OpenAPI', label: 'OpenAPI' },
        { value: 'Transfers_NIPInward', label: 'NIP Inward Transfer' },
        { value: 'InBranch', label: 'In-Branch' },
        { value: 'Transfers_ISWInward', label: 'ISW Inward Transfer' },
      ],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchEChannelTransactionReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchEChannelTransactionReport()
    },
  },
  mounted() {
    this.fetchEChannelTransactionReport()
  },
  methods: {
    ...mapActions({
      getEChannelTransactionReport: 'reports/getEChannelTransactionReport',
      downloadEChannelTransactionReport: 'reports/downloadEChannelTransactionReport',
      getBranchByCode: 'operations/getBranchByCode',
    }),

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchEChannelTransactionReport()
    },

    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.eChannelTransactionRules.validate().then(async success => {
        if (success) {
          this.fetchEChannelTransactionReport()
        }
      })
    },
    async fetchEChannelTransactionReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const filteredData = {
          ...this.filter,
          financialDateFrom: this.filter.financialDateFrom !== undefined || null ? toYearWithDate(this.filter.financialDateFrom) : null,
          financialDateTo: this.filter.financialDateTo !== undefined || null ? toYearWithDate(this.filter.financialDateTo) : null,
          startDate: this.filter.startDate !== undefined || null ? toYearWithDate(this.filter.startDate) : null,
          endDate: this.filter.endDate !== undefined || null ? toYearWithDate(this.filter.endDate) : null,
        }
        const response = await this.getEChannelTransactionReport({
          search: filteredData,
          page: this.currentPage,
          limit: this.perPage,
          permission: reportPermissions.VIEW_ECHANNEL_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to download E-Channel Transaction Report failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadEChannelTransactionReport({
          ...this.filter,
          financialDateFrom: this.filter.financialDateFrom !== undefined || null ? toYearWithDate(this.filter.financialDateFrom) : '',
          financialDateTo: this.filter.financialDateTo !== undefined || null ? toYearWithDate(this.filter.financialDateTo) : '',
          startDate: this.filter.startDate !== undefined || null ? toYearWithDate(this.filter.startDate) : '',
          endDate: this.filter.endDate !== undefined || null ? toYearWithDate(this.filter.endDate) : '',
          fileExportFormat: 1,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'E-Channel Transaction Report.csv')
          this.alertSuccess({ text: 'Your request to download E-Channel Transaction Report was successful' })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to download E-Channel Transaction Report failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadEChannelTransactionReport({
          ...this.filter,
          financialDateFrom: this.filter.financialDateFrom !== undefined || null ? toYearWithDate(this.filter.financialDateFrom) : '',
          financialDateTo: this.filter.financialDateTo !== undefined || null ? toYearWithDate(this.filter.financialDateTo) : '',
          startDate: this.filter.startDate !== undefined || null ? toYearWithDate(this.filter.startDate) : '',
          endDate: this.filter.endDate !== undefined || null ? toYearWithDate(this.filter.endDate) : '',
          fileExportFormat: 0,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
          FileSaver.saveAs(blob, 'E-Channel Transaction Report.xlsx')
          this.alertSuccess({ text: 'Your request to download E-Channel Transaction Report was successful' })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to download E-Channel Transaction Report failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
